import React from "react";
import { Link } from "react-router-dom";
import ReadMore from "../../components/common/ReadMore";
import { SCROLL_TO_PRICING_STATE } from "../../utils/scrollToPricing";
import AnalystAlertsGate from "./AnalystAlertsGate";
import { PostDetailBadgeCheckIcon } from "../PostDetail/PostDetailCommentIcons";
import { formatPostDate } from "../Education/educationUtils";
import { formatToLocalDate } from "../../utils";
import { truncateAlertPreview } from "./analystUtils";
import AnalystFeaturedVideo from "./AnalystFeaturedVideo";
import {
  AnalystArrowLeftIcon,
  AnalystArrowRightIcon,
  AnalystGraduationCapIcon,
} from "./AnalystIcons";
import {
  AnalystAvatar,
  AnalystBackLink,
  AnalystBioBlock,
  AnalystEducationCard,
  AnalystEducationDate,
  AnalystEducationGrid,
  AnalystEducationIconWrap,
  AnalystEducationInner,
  AnalystEducationRead,
  AnalystEducationSection,
  AnalystEducationTitle,
  AnalystEmptyState,
  AnalystGuestFooter,
  AnalystGuestFooterInner,
  AnalystHeroBioCol,
  AnalystHeroGrid,
  AnalystHeroInner,
  AnalystHeroSection,
  AnalystHeroShell,
  AnalystHeroVideoCol,
  AnalystIdentityMeta,
  AnalystIdentityRow,
  AnalystName,
  AnalystNameRow,
  AnalystPanel,
  AnalystPanelDesc,
  AnalystPanelHead,
  AnalystPanelTitle,
  AnalystPreviewDate,
  AnalystPreviewLink,
  AnalystPreviewList,
  AnalystPreviewTitle,
  AnalystRoleBadge,
  AnalystSeeAllLink,
  AnalystSplitGrid,
  AnalystSplitSection,
  AnalystVerifiedIcon,
  AnalystVideoFrame,
} from "./Analyst.styles";

const emptyCopy = {
  posts: {
    title: "No posts yet",
    message: "This analyst has not published member posts recently.",
  },
  alerts: {
    title: "No alerts yet",
    message: "Recent alerts from this analyst will appear here.",
  },
  education: {
    title: "No education yet",
    message: "Educational content from this analyst will appear here.",
  },
};

const AnalystProfileContent = ({
  profile,
  posts,
  alerts,
  educationalPosts,
  isGuest,
  showRenewalNotice,
  onSeeAllPosts,
  onSeeAllAlerts,
  onSeeAllEducation,
}) => {
  const videoId = profile?.featuredVideo?.mediaId;
  const posterUrl =
    profile?.avatar?.original ||
    profile?.avatar?.medium ||
    profile?.avatar?.small ||
    null;
  const visibleAlerts = alerts.filter((alert) => alert.content !== undefined);

  return (
    <>
      <AnalystHeroSection>
        <AnalystHeroInner>
          <AnalystBackLink to="/analysts">
            <AnalystArrowLeftIcon />
            Back to analysts
          </AnalystBackLink>

          <AnalystHeroShell>
            <AnalystHeroGrid>
              <AnalystHeroBioCol>
                <AnalystIdentityRow>
                  <AnalystAvatar
                    src={profile.avatar?.medium || profile.avatar?.small}
                    alt={profile.name}
                    width={144}
                    height={144}
                  />
                  <AnalystIdentityMeta>
                    <AnalystNameRow>
                      <AnalystName>{profile.name}</AnalystName>
                      <AnalystVerifiedIcon aria-label="Verified analyst">
                        <PostDetailBadgeCheckIcon />
                      </AnalystVerifiedIcon>
                    </AnalystNameRow>
                    <AnalystRoleBadge>Analyst</AnalystRoleBadge>
                  </AnalystIdentityMeta>
                </AnalystIdentityRow>

                {profile.bio != null && profile.bio !== "" && (
                  <AnalystBioBlock>
                    <ReadMore
                      text={profile.bio}
                      min={330}
                      ideal={340}
                      max={350}
                      readMoreText="Read more"
                    />
                  </AnalystBioBlock>
                )}
              </AnalystHeroBioCol>

              <AnalystHeroVideoCol>
                <AnalystVideoFrame>
                  <AnalystFeaturedVideo
                    mediaId={videoId}
                    posterUrl={posterUrl}
                    name={profile.name}
                  />
                </AnalystVideoFrame>
              </AnalystHeroVideoCol>
            </AnalystHeroGrid>
          </AnalystHeroShell>
        </AnalystHeroInner>
      </AnalystHeroSection>

      <AnalystSplitSection>
        <AnalystSplitGrid>
          <AnalystPanel>
            <AnalystPanelHead>
              <div>
                <AnalystPanelTitle>Posts</AnalystPanelTitle>
                <AnalystPanelDesc>
                  Latest member content from this analyst.
                </AnalystPanelDesc>
              </div>
              <AnalystSeeAllLink type="button" onClick={onSeeAllPosts}>
                See all
                <AnalystArrowRightIcon />
              </AnalystSeeAllLink>
            </AnalystPanelHead>

            {posts.length === 0 ? (
              <AnalystEmptyState>
                <strong>{emptyCopy.posts.title}</strong>
                <p>{emptyCopy.posts.message}</p>
              </AnalystEmptyState>
            ) : (
              <AnalystPreviewList>
                {posts.map((post) => (
                  <AnalystPreviewLink key={post.id} to={post.url}>
                    <AnalystPreviewTitle>{post.title}</AnalystPreviewTitle>
                    <AnalystPreviewDate>
                      {formatToLocalDate(post.publishedAt)}
                    </AnalystPreviewDate>
                  </AnalystPreviewLink>
                ))}
              </AnalystPreviewList>
            )}
          </AnalystPanel>

          <AnalystPanel>
            <AnalystPanelHead>
              <div>
                <AnalystPanelTitle>Alerts</AnalystPanelTitle>
                <AnalystPanelDesc>
                  Recent trade and market alerts.
                </AnalystPanelDesc>
              </div>
              <AnalystSeeAllLink type="button" onClick={onSeeAllAlerts}>
                See all
                <AnalystArrowRightIcon />
              </AnalystSeeAllLink>
            </AnalystPanelHead>

            {isGuest && (
              <AnalystAlertsGate variant="guest" analystName={profile.name} />
            )}

            {showRenewalNotice && (
              <AnalystAlertsGate
                variant="renew"
                subtitle="Renew your membership to continue receiving alerts."
              />
            )}

            {!isGuest && !showRenewalNotice && visibleAlerts.length === 0 && (
              <AnalystEmptyState>
                <strong>{emptyCopy.alerts.title}</strong>
                <p>{emptyCopy.alerts.message}</p>
              </AnalystEmptyState>
            )}

            {!isGuest && visibleAlerts.length > 0 && (
              <AnalystPreviewList>
                {visibleAlerts.map((alert) => (
                  <AnalystPreviewLink key={alert.id} to={`/alerts/${alert.id}`}>
                    <AnalystPreviewTitle>
                      {truncateAlertPreview(alert.content)}
                    </AnalystPreviewTitle>
                    <AnalystPreviewDate>
                      {formatToLocalDate(alert.createdAt)}
                    </AnalystPreviewDate>
                  </AnalystPreviewLink>
                ))}
              </AnalystPreviewList>
            )}
          </AnalystPanel>
        </AnalystSplitGrid>
      </AnalystSplitSection>

      <AnalystEducationSection>
        <AnalystEducationInner>
          <AnalystPanelHead>
            <div>
              <AnalystPanelTitle>Education</AnalystPanelTitle>
              <AnalystPanelDesc>
                Free educational content from this analyst.
              </AnalystPanelDesc>
            </div>
            <AnalystSeeAllLink type="button" onClick={onSeeAllEducation}>
              See all
              <AnalystArrowRightIcon />
            </AnalystSeeAllLink>
          </AnalystPanelHead>

          {educationalPosts.length === 0 ? (
            <AnalystEmptyState>
              <strong>{emptyCopy.education.title}</strong>
              <p>{emptyCopy.education.message}</p>
            </AnalystEmptyState>
          ) : (
            <AnalystEducationGrid>
              {educationalPosts.map((post) => (
                <AnalystEducationCard key={post.id} to={post.url}>
                  <AnalystEducationIconWrap>
                    <AnalystGraduationCapIcon />
                  </AnalystEducationIconWrap>
                  <AnalystEducationTitle>{post.title}</AnalystEducationTitle>
                  <AnalystEducationDate>
                    {formatPostDate(post.publishedAt)}
                  </AnalystEducationDate>
                  <AnalystEducationRead>
                    Read
                    <AnalystArrowRightIcon />
                  </AnalystEducationRead>
                </AnalystEducationCard>
              ))}
            </AnalystEducationGrid>
          )}
        </AnalystEducationInner>
      </AnalystEducationSection>

      {isGuest && (
        <AnalystGuestFooter>
          <AnalystGuestFooterInner>
            Want full alerts and member videos?{" "}
            <Link to="/" state={SCROLL_TO_PRICING_STATE}>
              See membership plans
            </Link>
          </AnalystGuestFooterInner>
        </AnalystGuestFooter>
      )}
    </>
  );
};

export default AnalystProfileContent;
